/**
 * Theme mode context — manages system/light/dark preference.
 *
 * - Persists choice in localStorage('themeMode')
 * - Listens to OS `prefers-color-scheme` when mode is 'system'
 * - Sets `data-theme` on <html> for CSS / Tailwind scoping
 * - Exposes `resolvedMode` ('light' | 'dark') and `setMode()`
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export type ThemeMode = 'system' | 'light' | 'dark'
export type ResolvedMode = 'light' | 'dark'

interface ThemeContextValue {
  mode: ThemeMode
  resolvedMode: ResolvedMode
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue>({
  mode: 'system',
  resolvedMode: 'light',
  setMode: () => {},
})

const STORAGE_KEY = 'themeMode'

function getSystemPreference(): ResolvedMode {
  if (typeof window === 'undefined') {
    return 'light'
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
    return 'system'
  })

  const [systemPref, setSystemPref] = useState<ResolvedMode>(getSystemPreference)

  // Listen for OS preference changes
  useEffect(() => {
    const mql = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light')
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const resolvedMode: ResolvedMode = mode === 'system' ? systemPref : mode

  // Apply data-theme attribute on <html>
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedMode)
  }, [resolvedMode])

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode)
    localStorage.setItem(STORAGE_KEY, newMode)
  }, [])

  const value = useMemo(() => ({ mode, resolvedMode, setMode }), [mode, resolvedMode, setMode])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useThemeMode() {
  return useContext(ThemeContext)
}
